import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { RoutineRequest } from '~/common/entities/Routines/RoutineRequest';
import { Routine } from '~/common/entities/Routines/RoutineTypes';
import { toApiDateFromDate } from '~/common/utils/timeUtils';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

export enum RoutineKeys {
  ROUTINES = 'ROUTINES',
  ROUTINE = 'ROUTINE',
  ROUTINE_STATISTICS = 'ROUTINE_STATISTICS',
}

export interface UpdateRoutineRequest {
  name: string;
  duration: number;
  description: string;
  employees: string[];
}

export interface RoutineStatistics {
  employees: number[];
  durations: {
    employee: number;
    duration: number;
  }[];
}

const RoutineQueries = {
  async getRoutines() {
    const res = await axios.get<Routine[]>(`/routines/`);
    return unwrap(res);
  },
  async getRoutine(routine: IdProvider) {
    if (!routine?.id) {
      return null;
    }

    return unwrap(await axios.get<Routine>(`/routine/${routine?.id}/`));
  },
  async getRoutineStatistics(
    routine: IdProvider,
    start: Date | null | undefined,
    end: Date | null | undefined,
  ) {
    if (!routine?.id) {
      return null;
    }
    const res = await axios.get<RoutineStatistics>(
      `/routine/statistics/?routine=${routine.id}&start=${toApiDateFromDate(
        start,
      )}&end=${toApiDateFromDate(end)}`,
    );
    return unwrap(res);
  },
};

const RoutineMutations = {
  async createRoutine(req: RoutineRequest) {
    const res = await axios.post<Routine>('/routine/', req);
    return unwrap(res);
  },
  async updateRoutine(routine: IdProvider, req: UpdateRoutineRequest) {
    if (!routine?.id) {
      return null;
    }
    const res = await axios.put<Routine>(`/routine/${routine.id}/`, req);
    return unwrap(res);
  },
  async deleteRoutine(routine: IdProvider) {
    if (!routine?.id) {
      return;
    }
    const res = await axios.delete<void>(`/routine/${routine.id}/`);
    return unwrap(res);
  },
};

const RoutineHooks = {
  useRoutinesQuery(options?: UseQueryOptions<Routine[]>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [RoutineKeys.ROUTINES],
      queryFn: RoutineQueries.getRoutines,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([RoutineKeys.ROUTINES]),
    ];
    return QueryResultAndInvalidate;
  },
  useRoutineQuery(
    routine: IdProvider,
    options?: UseQueryOptions<Routine | null>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [RoutineKeys.ROUTINE, routine],
      queryFn: () => RoutineQueries.getRoutine(routine),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([RoutineKeys.ROUTINE, routine]),
    ];
    return QueryResultAndInvalidate;
  },
  useRoutineStatisticsQuery(
    routine: IdProvider,
    start?: Date | null | undefined,
    end?: Date | null | undefined,
    options?: UseQueryOptions<RoutineStatistics | null>,
  ) {
    const queryClient = useQueryClient();
    const keys: unknown[] = [RoutineKeys.ROUTINE_STATISTICS, routine?.id];
    if (start) {
      keys.push(start.toISOString());
    }
    if (end) {
      keys.push(end.toISOString());
    }
    const result = useQuery({
      queryKey: keys,
      queryFn: () => RoutineQueries.getRoutineStatistics(routine, start, end),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries(keys),
    ];
    return QueryResultAndInvalidate;
  },
  useCreateRoutineMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation(RoutineMutations.createRoutine, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([RoutineKeys.ROUTINES]);
        await queryClient.invalidateQueries([
          RoutineKeys.ROUTINE,
          { id: data.id },
        ]);
      },
    });
    return mutation;
  },
  useUpdateRoutineMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation(
      ({ id, ...req }: IdProvider & UpdateRoutineRequest) =>
        RoutineMutations.updateRoutine({ id }, req),
      {
        onSuccess: async (data) => {
          await queryClient.invalidateQueries([RoutineKeys.ROUTINES]);
          await queryClient.invalidateQueries([
            RoutineKeys.ROUTINE,
            { id: data?.id },
          ]);
          await queryClient.invalidateQueries([
            RoutineKeys.ROUTINE_STATISTICS,
            data?.id,
          ]);
        },
      },
    );
    return mutation;
  },
  useDeleteRoutineMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation(RoutineMutations.deleteRoutine, {
      onSuccess: async (_data, idProvider) => {
        await queryClient.invalidateQueries([RoutineKeys.ROUTINES]);
        await queryClient.invalidateQueries([RoutineKeys.ROUTINE, idProvider]);
        await queryClient.invalidateQueries([
          RoutineKeys.ROUTINE_STATISTICS,
          idProvider?.id,
        ]);
      },
    });
    return mutation;
  },
};

export const RoutineApi = {
  queries: RoutineQueries,
  mutations: RoutineMutations,
  hooks: RoutineHooks,
};
